import { useShowDialog } from '@experiences/util';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import {
    useForm,
    useWatch,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useUiSnackBar } from '../../../../common/hooks/useUiSnackBar';
import type {
    ByoLlmConfiguration,
    ByoOperationGroupConfiguration,
    ByoProductLlmConfiguration,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    ByoLlmConfigurationType,
    ModelsConfigurationOption,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    checkIsFormComplete,
    getConnectorTemplateFromTags,
} from '../../../../services/llm-gateway/LlmGateway.utils';
import {
    addByoProductLlmConfiguration,
    updateByoProductLlmConfiguration,
    validateByoProductLlmConfiguration,
} from '../../../../services/llm-gateway/LlmGatewayService';
import {
    detectIncompleteLlmConfigurationErrors,
    processValidationResults,
} from '../AddEditConfigurationComponent';
import type { AddEditConfigurationFormData } from '../AddEditConfigurationViewModel';
import { ConnectedModelsIssuesDialogBody } from '../subcomponents/ConnectedModelsIssuesDialogBody';
import { ConnectedModelsSuccessDialogBody } from './ConnectedModelsSuccessDialogBody';

export const detectModelOverlapErrors = (
    featureConfig: ByoOperationGroupConfiguration,
    llmConfigs: Array<Partial<ByoLlmConfiguration>>,
    originalLlmConfigs: ByoLlmConfiguration[],
    inHouseModelNames: string[],
    customerModelNames: string[]): [string, string] | null => {
    // No overlap checks needed for AnyModel configuration
    if (featureConfig.modelsConfigurationOption === ModelsConfigurationOption.AnyModel) {
        return null;
    }

    for (let i = 0; i < llmConfigs.length; i++) {
        const modelName = llmConfigs[i].desiredModel;
        const initialModelName = originalLlmConfigs.at(i)?.desiredModel;
        const isCustomAlias = llmConfigs[i].isCustomAlias;
        const initialIsCustomAlias = !inHouseModelNames.includes(originalLlmConfigs.at(i)?.desiredModel ?? '');

        if (!modelName) {
            continue;
        }

        if (modelName === initialModelName && isCustomAlias === initialIsCustomAlias) {
            continue;
        }

        if (!isCustomAlias) {
            continue;
        }

        if (inHouseModelNames.includes(modelName)) {
            return [ 'CLIENT_BYO_LM_CONNECTED_MODEL_NAME_OVERLAPPING_UIPATH_MODEL_ERROR', modelName ];
        }

        if (customerModelNames?.includes(modelName) ?? false) {
            return [ 'CLIENT_BYO_LM_CONNECTED_MODEL_NAME_OVERLAPPING_BYO_MODEL_ERROR', modelName ];
        }
    }

    return null;
};

interface UseByoConfigurationFormOptions {
    organizationId: string;
    tenantId: string;
    configurationId?: string;
    productConfigurations?: Record<string, Record<string, any>>;
    llmToConnectorTypeMapping: Record<string, any>;
    availableLlms?: Record<string, Record<string, string[]>>;
    configuredLlms?: Record<string, Record<string, string[]>>;
    editedProductLlmConfiguration?: ByoProductLlmConfiguration;
    onSuccess: () => void;
}

export const useByoConfigurationForm = ({
    organizationId,
    tenantId,
    configurationId,
    productConfigurations,
    llmToConnectorTypeMapping,
    availableLlms,
    configuredLlms,
    editedProductLlmConfiguration,
    onSuccess,
}: UseByoConfigurationFormOptions) => {
    const { formatMessage: translate } = useIntl();
    const { createNotification } = useUiSnackBar();
    const createDialog = useShowDialog();

    const [ isConfigurationTestingCommencing, setIsConfigurationTestingCommencing ] = useState(false);
    const [ isConfigurationTested, setIsConfigurationTested ] = useState(false);

    const formMethods = useForm<AddEditConfigurationFormData>({
        mode: 'onTouched',
        defaultValues: {
            folderKey: null,
            product: '',
            feature: '',
            llmConfigurations: {},
        },
    });

    const {
        control,
        getValues,
        handleSubmit,
        resetField,
        setValue,
        setError,
        clearErrors,
        reset,
        formState: {
            errors, isValid, isSubmitting,
        },
    } = formMethods;

    const {
        folderKey,
        product,
        feature,
        llmConfigurations,
    } = useWatch({ control });

    const featureHasAllModelsConfigurationConstraint = useMemo(() => {
        if (!productConfigurations || !product || !feature) {
            return false;
        }
        return productConfigurations[product]?.[feature]?.modelsConfigurationOption === ModelsConfigurationOption.AllModels;
    }, [ feature, product, productConfigurations ]);

    const isFormComplete = useMemo(() => checkIsFormComplete(llmConfigurations), [ llmConfigurations ]);

    const configurationMode = useMemo(() => {
        if (!productConfigurations || !product || !feature || !llmConfigurations || Object.keys(llmConfigurations).length === 0) {
            return undefined;
        }

        if (productConfigurations[product]?.[feature]?.modelsConfigurationOption !== ModelsConfigurationOption.AnyModelWithOwnAdditions) {
            return 'replace';
        }

        const featureLlms = productConfigurations[product][feature].models.map((modelConfig: any) => modelConfig.model);
        const desiredModel = Object.keys(llmConfigurations)[0];

        if (!featureLlms.includes(desiredModel)) {
            return 'add';
        }

        return 'replace';
    }, [ feature, llmConfigurations, product, productConfigurations ]);

    const getCurrentByoProductLlmConfigurationFromValues = useCallback((): Partial<ByoProductLlmConfiguration> => {
        const values = getValues();
        return {
            id: configurationId,
            organizationId,
            tenantId,
            folderId: values.folderKey ?? undefined,
            name: 'placeholder-name',
            product: values.product,
            operationGroupName: values.feature,
            llmConfigurations: Object.values(values.llmConfigurations).map(llmConfig => {
                const connectorTemplate = getConnectorTemplateFromTags(llmConfig.connectorTags);
                const config: Partial<ByoLlmConfiguration> = {
                    id: llmConfig.id,
                    organizationId: llmConfig.organizationId ?? organizationId,
                    tenantId: llmConfig.tenantId ?? tenantId,
                    connectionId: llmConfig.connectionId,
                    connectorType: llmConfig.connectorType,
                    apiFlavor: llmConfig.apiFlavor,
                    defaultModel: llmConfig.defaultModel ?? llmConfig.desiredModel,
                    desiredModel: llmConfig.desiredModel,
                    customerModel: llmConfig.customerModel,
                    customFieldMappings: llmConfig.customFieldMappings,
                    configurationType: ByoLlmConfigurationType.SelfServe,
                };
                if (connectorTemplate) {
                    config.selfServeByoConfigParameters = { connectorTemplate };
                }
                return config as ByoLlmConfiguration;
            }),
            enabled: true,
        };
    }, [ configurationId, getValues, organizationId, tenantId ]);

    const validateConfiguration = useCallback(async (request: Partial<ByoProductLlmConfiguration>) => {
        try {
            setIsConfigurationTestingCommencing(true);
            const modelsCompatibilityReports = await validateByoProductLlmConfiguration(
                organizationId,
                tenantId,
                request,
                configurationMode,
            );
            const result = processValidationResults(
                modelsCompatibilityReports,
                request.product ?? '',
                request.operationGroupName ?? '',
                llmConfigurations as Record<string, Partial<ByoLlmConfiguration>> | undefined,
            );

            if (result.hasIssues) {
                await createDialog({
                    title: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_INVALID_CONFIGURATION_CONNECTED_MODELS_ISSUES_TITLE' }),
                    body: (
                        <ConnectedModelsIssuesDialogBody
                            modelsWithReportedIssues={result.modelsWithReportedIssues} />
                    ),
                    icon: 'error',
                    hideActions: true,
                });
            } else {
                if (llmConfigurations) {
                    setValue('llmConfigurations', llmConfigurations as Record<string, Partial<ByoLlmConfiguration>>);
                }
                setIsConfigurationTested(true);

                await createDialog({
                    title: '',
                    customDialogContent: ConnectedModelsSuccessDialogBody,
                    hideActions: true,
                    unclosable: true,
                });
            }
        } catch (error: any) {
            createNotification(translate(
                { id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_FAILED_TO_VALIDATE' },
                { error: error?.response?.data?.message ?? error?.message },
            ), 'error');
        } finally {
            setIsConfigurationTestingCommencing(false);
        }
    }, [ createDialog, createNotification, configurationMode, llmConfigurations, organizationId, setValue, tenantId, translate ]);

    const onTest = useCallback(async () => {
        const request = getCurrentByoProductLlmConfigurationFromValues();
        if (!isValid) {
            return;
        }
        request.name = `validation-${request.operationGroupName}-${Date.now()}`;
        await validateConfiguration(request);
    }, [ getCurrentByoProductLlmConfigurationFromValues, isValid, validateConfiguration ]);

    const onSubmit = useCallback(async () => {
        const request = getCurrentByoProductLlmConfigurationFromValues();
        if (!isValid || !isConfigurationTested) {
            return;
        }
        request.name = `${request.operationGroupName}-${Date.now()}`;

        try {
            if (configurationId) {
                await updateByoProductLlmConfiguration(organizationId, tenantId, configurationId, request);
            } else {
                await addByoProductLlmConfiguration(organizationId, tenantId, request);
            }
            onSuccess();
        } catch (error: any) {
            const stringIdentifier = configurationId
                ? 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_FAILED_TO_EDIT'
                : 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_FAILED_TO_ADD';
            createNotification(translate({ id: stringIdentifier }, { error: error?.response?.data?.message ?? error?.message }), 'error');
        }
    }, [ getCurrentByoProductLlmConfigurationFromValues, isConfigurationTested, isValid, configurationId, organizationId, tenantId, onSuccess, createNotification, translate ]);

    const validateLlmConfigurationsRequiredFields = useCallback(async (llmConfigs: Record<string, Partial<ByoLlmConfiguration>> | undefined) => {
        const errorId = detectIncompleteLlmConfigurationErrors(llmConfigs, false);
        if (errorId) {
            return translate({ id: errorId });
        }
        clearErrors('llmConfigurations');
        return true;
    }, [ clearErrors, translate ]);

    const validateLlmConfigurationsModelNameOverlap = useCallback(async (llmConfigs: Record<string, Partial<ByoLlmConfiguration>> | undefined) => {
        const featureConfig = productConfigurations?.[product ?? '']?.[feature ?? ''];
        if (!featureConfig || !llmConfigs || Object.values(llmConfigs).length === 0) {
            return true;
        }

        const originalLlmConfigs = editedProductLlmConfiguration?.llmConfigurations ?? [];
        const inHouseModelNames = Object.keys(llmToConnectorTypeMapping);
        const customerModelNames = configuredLlms?.[product ?? '']?.[feature ?? ''] ?? [];

        const result = detectModelOverlapErrors(featureConfig, Object.values(llmConfigs), originalLlmConfigs, inHouseModelNames, customerModelNames);
        if (result) {
            const [ errorId, modelName ] = result;
            return translate({ id: errorId }) + `,${modelName}`;
        }
        clearErrors('llmConfigurations');
        return true;
    }, [ clearErrors, configuredLlms, editedProductLlmConfiguration?.llmConfigurations, feature, llmToConnectorTypeMapping, product, productConfigurations, translate ]);

    const notifyFieldChanges = useCallback(() => {
        setIsConfigurationTested(false);
    }, []);

    const handleFolderChange = useCallback(() => {
        if (featureHasAllModelsConfigurationConstraint && product && feature) {
            setValue('llmConfigurations', availableLlms?.[product]?.[feature]
                ?.reduce((acc, llm) => {
                    acc[llm] = {
                        desiredModel: llm,
                        defaultModel: llm,
                    };
                    return acc;
                }, {} as Record<string, Partial<ByoLlmConfiguration>>) ?? {});
        } else {
            resetField('llmConfigurations');
        }
        notifyFieldChanges();
    }, [ availableLlms, feature, featureHasAllModelsConfigurationConstraint, notifyFieldChanges, product, resetField, setValue ]);

    return {
        // Form methods
        control,
        getValues,
        handleSubmit,
        resetField,
        setValue,
        setError,
        clearErrors,
        reset,
        errors,
        isValid,
        isSubmitting,

        // Watched fields
        folderKey,
        product,
        feature,
        llmConfigurations,

        // Computed
        featureHasAllModelsConfigurationConstraint,
        isFormComplete,
        configurationMode,

        // Callbacks
        handleFolderChange,

        // Test/Save
        isConfigurationTestingCommencing,
        isConfigurationTested,
        notifyFieldChanges,
        onTest,
        onSubmit,

        // Validation
        validateLlmConfigurationsRequiredFields,
        validateLlmConfigurationsModelNameOverlap,
    };
};
